/* eslint-disable complexity */

/* eslint-disable max-lines-per-function */
import { createAsyncThunk } from '@reduxjs/toolkit';
import { getCurrentEntityId } from 'src/models/details/selectors';
import { setCurrentEntityId } from 'src/models/details/slice';
import { fetchWorkflowDetails } from 'src/models/details/thunk';
import { getState } from 'src/store';
import axios from 'src/workspace/axios';
import { CASE, REVYRE, TAB_STATUS_KEYS } from './constant';
import { getWorkflowCode } from './profileCard/queries';
import { getRevyreReferredModules } from './queries';
import {
  clearDetailsState,
  setAuditHistory,
  setHistory,
  setNotification,
  setPhoneCallDetails,
  setReferredModules,
  setRelatedRecords,
  setStatus,
  setSupervisorReviews,
  setTrails,
} from './slice';

export const callSelectedEntity = createAsyncThunk(
  'clickToCall',
  async ({ props, option, details }: any, { dispatch }) => {
    try {
      if (props.revyre) {
        const { id, profileId } = option;
        const basePayload = { profileId, phoneId: id };
        const { domainType, workflowType, entityId } = props;
        const inputsMapWorkflowId = getWorkflowCode(
          details?.inputsMap?.workflowId,
        );
        const associatedWorkflow = details?.inputsMap?.workflowType;

        const payload =
          domainType === 'activity'
            ? {
                workflowId: inputsMapWorkflowId,
                workflowType: associatedWorkflow || domainType,
                activityId: entityId,
              }
            : {
                workflowType: workflowType ?? domainType,
                workflowId: entityId,
              };

        const response = await axios.post(
          `/vymo/v3/workflow/phonecall`,
          { ...basePayload, ...payload },
          {
            headers: {
              'x-vymo-device-type': 'web',
            },
          },
        );

        const { activityId, message } = response.data?.payload || {};

        dispatch(setPhoneCallDetails({ activityId }));
        dispatch(
          setNotification({
            message,
            variant: 'info',
            messageId: String(Date.now()),
          }),
        );
        return true;
      }
      return true;
    } catch (err: any) {
      const errDetails = err?.response?.data?.details;
      const description = errDetails?.description;
      const subErrorCode = errDetails?.subErrorCode;
      if (
        REVYRE.COLLECT_IQ.STATUS_ERROR_CODE_WITH_DESCRIPTION.includes(
          subErrorCode,
        )
      ) {
        // eslint-disable-next-line no-console
        console.log(description);
      } else {
        // eslint-disable-next-line no-console
        console.log(err);
      }

      return false;
    }
  },
);

export const fetchSuspendedCases = createAsyncThunk(
  'suspendedCases',
  async ({ payload }: any, { dispatch }) => {
    try {
      dispatch(
        setStatus({
          [TAB_STATUS_KEYS.SUSPENDED_CASES]: 'loading',
        }),
      );
      const res = await axios.post(
        `/vymo/v3/workflow/ccase/suspended/list`,
        payload,
      );
      const cases = res?.data?.payload?.result || [];
      const meta = {
        maxPages: res?.data?.payload?.maxPages,
        totalCount: res?.data?.payload?.totalCount,
      };
      dispatch(
        setRelatedRecords({
          suspendedCases: {
            list: cases.map((c) => c.mutatedEntity || {}),
            meta,
          },
        }),
      );
      dispatch(
        setStatus({
          [TAB_STATUS_KEYS.SUSPENDED_CASES]: 'done',
        }),
      );
    } catch (e) {
      dispatch(
        setStatus({
          [TAB_STATUS_KEYS.SUSPENDED_CASES]: 'error',
        }),
      );
    }
  },
);

export const fetchActiveCases = createAsyncThunk(
  'activeCases',
  async ({ payload }: any, { dispatch }) => {
    try {
      dispatch(
        setStatus({
          [TAB_STATUS_KEYS.ACTIVE_CASES]: 'loading',
        }),
      );
      const res = await axios.post(
        `/vymo/v3/workflow/ccase/nonsuspended/list`,
        payload,
      );
      const cases = res?.data?.payload?.result || [];
      const meta = {
        maxPages: res?.data?.payload?.maxPages,
        totalCount: res?.data?.payload?.totalCount,
      };
      dispatch(
        setRelatedRecords({
          activeCases: {
            list: cases.map((c) => c.mutatedEntity || {}),
            meta,
          },
        }),
      );
      dispatch(
        setStatus({
          [TAB_STATUS_KEYS.ACTIVE_CASES]: 'done',
        }),
      );
    } catch (e) {
      dispatch(
        setStatus({
          [TAB_STATUS_KEYS.ACTIVE_CASES]: 'error',
        }),
      );
    }
  },
);

export const fetchRevyreHistory = createAsyncThunk(
  'revyreHistory',
  async ({ props }: { props: any }, { dispatch }) => {
    try {
      dispatch(
        setStatus({
          [TAB_STATUS_KEYS.HISTORY]: 'loading',
        }),
      );
      const { workflowType, domainType, entityId } = props;
      const res = await axios.post('/vymo/v3/workflow/auditConsumer/history', {
        serviceType: workflowType || domainType,
        serviceId: entityId,
      });
      dispatch(setHistory(res.data.payload || []));
      dispatch(
        setStatus({
          [TAB_STATUS_KEYS.HISTORY]: 'done',
        }),
      );
    } catch (e) {
      dispatch(
        setStatus({
          [TAB_STATUS_KEYS.HISTORY]: 'error',
        }),
      );
    }
  },
);

export const fetchSupervisorReviews = createAsyncThunk(
  'supervisorReview',
  async (inputProps: any, { dispatch }) => {
    try {
      const props = inputProps ?? {};
      dispatch(
        setStatus({
          [TAB_STATUS_KEYS.SUPERVISOR_REVIEW]: 'loading',
        }),
      );
      dispatch(setSupervisorReviews([]));
      const { entityId } = props;
      const response = await axios.post(
        '/vymo/v3/workflow/supervisorReview/list',
        {
          filters: {
            workflowId: entityId,
          },
        },
        {
          headers: {
            'x-vymo-device-type': 'web',
          },
        },
      );

      const supervisorReviews = response?.data?.payload?.result;
      dispatch(
        setSupervisorReviews(
          Array.isArray(supervisorReviews) ? supervisorReviews : [],
        ),
      );
      dispatch(
        setStatus({
          [TAB_STATUS_KEYS.SUPERVISOR_REVIEW]: 'done',
        }),
      );
    } catch (e) {
      dispatch(
        setStatus({
          [TAB_STATUS_KEYS.SUPERVISOR_REVIEW]: 'error',
        }),
      );
    }
  },
);

export const fetchTrails = createAsyncThunk(
  'trails',
  async ({ props, payload }: any, { dispatch }) => {
    try {
      const { workflowType } = props;
      dispatch(
        setStatus({
          [TAB_STATUS_KEYS.TRAILS]: 'loading',
        }),
      );

      const response = await axios.post(
        `/vymo/v3/workflow/${workflowType || CASE}/activity/list`,
        payload,
      );
      const trails = response?.data?.payload?.result || [];
      const meta = {
        maxPages: response?.data?.payload?.maxPages,
        totalCount: response?.data?.payload?.totalCount,
      };
      dispatch(
        setTrails({
          list: trails.map((trail) => trail.mutatedEntity || {}),
          meta,
        }),
      );

      dispatch(
        setStatus({
          [TAB_STATUS_KEYS.TRAILS]: 'done',
        }),
      );
    } catch (e) {
      dispatch(
        setStatus({
          [TAB_STATUS_KEYS.TRAILS]: 'error',
        }),
      );
    }
  },
);

export const fetchRelatedRecords = createAsyncThunk(
  'relatedRecords',
  async (
    {
      referredModules = [],
      props,
    }: {
      referredModules: {
        code: string;
        name: string;
        size: any;
        key?: string;
      }[];
      props: any;
    },
    { dispatch },
  ) => {
    try {
      dispatch(
        setStatus({
          [TAB_STATUS_KEYS.RELATED_RECORDS]: 'loading',
        }),
      );
      if (props.revyre) {
        referredModules.forEach(async (module) => {
          if (module.size) {
            const res = await axios.post(
              `/vymo/v3/workflow/${module.code}/list`,
              {
                filters: {
                  [module.key || 'workflowId']: props.entityId,
                },
                page: {
                  pageSize: module.size,
                  pageNumber: 1,
                },
              },
            );
            const list = res?.data?.payload?.result || [];
            dispatch(
              setRelatedRecords({
                [module.code]: {
                  list,
                },
              }),
            );
          }
        });
      }
      dispatch(
        setStatus({
          [TAB_STATUS_KEYS.RELATED_RECORDS]: 'done',
        }),
      );
    } catch (e) {
      dispatch(
        setStatus({
          [TAB_STATUS_KEYS.RELATED_RECORDS]: 'error',
        }),
      );
    }
  },
);

export const fetchEntityDetails = createAsyncThunk(
  'details',
  async ({ ...props }: any, { dispatch }) => {
    try {
      if (!props.noRelatedRecords) {
        dispatch(
          setStatus({
            [TAB_STATUS_KEYS.DETAILS]: 'loading',
          }),
        );
      }

      if (props.revyre) {
        const { workflowType, domainType, entityId } = props;

        const response = await fetchWorkflowDetails({
          workflowId: entityId,
          domainType,
          workflowType,
          dispatch,
        });

        const associatedWorkflow =
          response?.data?.payload?.mutatedEntity?.workflowType;
        const associatedWorkflowId = getWorkflowCode(
          response?.data?.payload?.mutatedEntity?.workflowId,
        );
        if (associatedWorkflowId && workflowType !== 'supervisorReview') {
          dispatch(
            fetchEntityDetails({
              revyre: true,
              domainType: 'workflow',
              workflowType: associatedWorkflow,
              entityId: associatedWorkflowId,
              noRelatedRecords: true,
            }),
          );
        }

        if (!props.noRelatedRecords) {
          const referredModules = getRevyreReferredModules(response?.data);
          dispatch(setReferredModules(referredModules));
          if (referredModules?.length) {
            dispatch(
              fetchRelatedRecords({
                referredModules: referredModules.filter(
                  (module) => module.workflowType !== CASE,
                ),
                props,
              }),
            );
          }

          if (props.workflowType === 'loan') {
            const caseReferredModule = referredModules.find(
              (module) => module.code === CASE,
            );
            if (caseReferredModule) {
              dispatch(
                fetchActiveCases({
                  payload: {
                    filters: {
                      [caseReferredModule.key || 'lan']: props.entityId,
                    },
                    page: { pageNumber: 1, pageSize: 10 },
                  },
                }),
              );
              dispatch(
                fetchSuspendedCases({
                  payload: {
                    filters: {
                      [caseReferredModule.key || 'lan']: props.entityId,
                    },
                    page: { pageNumber: 1, pageSize: 10 },
                  },
                }),
              );
            }
          }
        }
      }
      if (!props.noRelatedRecords) {
        dispatch(
          setStatus({
            [TAB_STATUS_KEYS.DETAILS]: 'done',
          }),
        );
      }
    } catch (e) {
      if (!props.noRelatedRecords) {
        dispatch(
          setStatus({
            [TAB_STATUS_KEYS.DETAILS]: 'error',
          }),
        );
      }
    }
  },
);

const fetchAuditHistory = createAsyncThunk(
  'auditHistory',
  async ({ ...props }: any, { dispatch }) => {
    try {
      dispatch(
        setStatus({
          [TAB_STATUS_KEYS.AUDIT_HISTORY]: 'loading',
        }),
      );
      const serviceType = props.workflowType || props.domainType;
      dispatch(setAuditHistory({ [serviceType]: [] }));
      if (props.revyre) {
        const { workflowType, domainType, entityId } = props;
        const auditServiceType = workflowType || domainType;
        const res = await axios.post(
          '/vymo/v3/workflow/auditConsumer/audits',
          { serviceType: auditServiceType, serviceId: entityId },
          {
            headers: {
              'x-vymo-device-type': 'web',
            },
          },
        );
        const audits = res?.data?.payload ?? [];
        dispatch(setAuditHistory({ [auditServiceType]: audits }));
      }
      dispatch(
        setStatus({
          [TAB_STATUS_KEYS.AUDIT_HISTORY]: 'done',
        }),
      );
    } catch (e) {
      dispatch(
        setStatus({
          [TAB_STATUS_KEYS.AUDIT_HISTORY]: 'error',
        }),
      );
    }
  },
);

const onUpdate = async ({ dispatch, props = {} }: any) => {
  const { revyre, workflowType, entityId } = props;
  const state = getState();

  if (getCurrentEntityId(state) === entityId) {
    return;
  }

  dispatch(setCurrentEntityId(props.entityId));
  dispatch(fetchEntityDetails({ ...props }));
  dispatch(fetchAuditHistory({ ...props }));
  if (revyre && workflowType === CASE) {
    dispatch(fetchSupervisorReviews({ ...props }));
    dispatch(
      fetchTrails({
        props,
        payload: {
          filters: { workflowId: entityId },
        },
      }),
    );
  }
};

const onActivate = ({ dispatch }: any) => {
  dispatch(clearDetailsState());
};

const onDeactivate = () => {};

export default { onActivate, onDeactivate, onUpdate };
